/*
 * Onboarding 002 — state and pure logic.
 *
 * Ported from the React implementation at reformly_web/lib and components/onboarding002.
 * The functions below are the ones that carried real bugs, so they are moved across
 * deliberately unchanged rather than rewritten:
 *
 *   isoDate()        — must use LOCAL parts. toISOString() converts to UTC first, so a
 *                      date created at local midnight east of UTC rolls back a day. A
 *                      birthday entered as 08.11.2002 in IST was stored as 2002-11-07.
 *   setWeightUnit()  — converts BOTH weights together. D4 computes `current - goal` and
 *                      the target floor compares them, so a kg/lb mix is silently wrong.
 *   mapPayload()     — enum values go up SCREAMING_SNAKE; height and weight go up RAW in
 *                      the entered unit alongside their unit field. The server stores the
 *                      submitted number as-is and only its BMI calc normalises.
 *
 * Loaded before quiz-002-screens.js and quiz-002.js. No build step: edit, commit, push.
 */
window.ReformlyQuiz = window.ReformlyQuiz || {};

(function (NS) {
  'use strict';

  /* ─── Store ──────────────────────────────────────────────────────────────── */

  var KEY = 'onboarding002-storage';
  var VERSION = 2;

  function blankState() {
    return {
      auth: {
        email: '', quizCountry: null, userId: null, isVerified: false,
        onboardingCompleted: false, termsAccepted: false, healthDataConsent: false,
        referralCode: null, affiliateCode: null,
      },
      aboutYou: {
        ageGroup: null, birthday: null, mainGoal: null, idealWeightPeriod: null,
        focusAreas: [], bodyType: null, dreamBodyShape: null,
        pilatesExperience: null, weightPattern: null,
        physicalStruggles: [], reformerExperience: null, reformerSkipReasons: [],
      },
      metrics: {
        height: { value: null, unit: 'cm' },
        currentWeight: { value: null, unit: 'kg' },
        goalWeight: { value: null, unit: 'kg' },
      },
      lifestyle: {
        typicalDay: null, energyLevel: null, workoutFrequency: null, sleepDuration: null,
        flexibilityLevel: null, stairsBreathLevel: null, postureLevel: null,
        walkFrequency: null, workSchedule: null, dailySittingTime: null,
        dailyWaterIntake: null, foodHabits: [], weightGainTriggers: [],
        marketingEmailsOptIn: null,
      },
      nutrition: { breakfastTime: null, lunchTime: null, dinnerTime: null, dietType: null },
      profile: { username: '', bio: '', name: '' },
      plan: { displayName: '', secondaryGoals: [], specialEvent: null, specialEventDate: null },
      stepIndex: 0,
    };
  }

  var state = blankState();

  function load() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      // Anything from before v2 belongs to a different set of questions — discard it and
      // let the user start over rather than resume into a flow that no longer matches.
      if (!saved || saved.version !== VERSION || !saved.state) return;
      state = Object.assign(blankState(), saved.state);
      // Dates survive JSON as strings.
      if (state.aboutYou.birthday) state.aboutYou.birthday = new Date(state.aboutYou.birthday);
      if (state.plan.specialEventDate) state.plan.specialEventDate = new Date(state.plan.specialEventDate);
    } catch (e) {
      /* corrupt storage — start clean rather than half-restored */
    }
  }

  function save() {
    try {
      localStorage.setItem(KEY, JSON.stringify({ version: VERSION, state: state }));
    } catch (e) {}
  }

  /** Shallow-merges into one section and persists. `set('auth', { email })`. */
  function set(section, patch) {
    Object.assign(state[section], patch);
    save();
  }

  function reset() {
    state = blankState();
    try { localStorage.removeItem(KEY); } catch (e) {}
  }

  /* ─── Units and metrics ──────────────────────────────────────────────────── */

  var LB_PER_KG = 0.453592;

  function toFeetInches(totalInches) {
    return { feet: Math.floor(totalInches / 12), inches: Math.round(totalInches % 12) };
  }

  /** Height is stored as total INCHES in imperial — nobody reads "72 in" as a height. */
  function formatHeight(value, unit) {
    if (value == null) return '—';
    if (unit === 'cm') return value + ' cm';
    var fi = toFeetInches(value);
    return fi.feet + 'ft ' + fi.inches + 'in';
  }

  function setHeightUnit(unit) {
    var h = state.metrics.height;
    var v = h.value == null ? (unit === 'cm' ? 168 : 66) : h.value;
    if (h.unit === unit) return;
    var next = unit === 'cm' ? Math.round(v * 2.54) : Math.round(v / 2.54);
    set('metrics', { height: { value: next, unit: unit } });
  }

  /**
   * Current and goal weight must always share a unit — see the header note.
   */
  function setWeightUnit(unit) {
    var m = state.metrics;
    if (m.currentWeight.unit === unit) return;
    var conv = function (v) {
      if (v == null) return null;
      return unit === 'kg' ? Math.round(v * LB_PER_KG) : Math.round(v / LB_PER_KG);
    };
    set('metrics', {
      currentWeight: { value: conv(m.currentWeight.value), unit: unit },
      goalWeight: { value: conv(m.goalWeight.value), unit: unit },
    });
  }

  function heightMetres() {
    var h = state.metrics.height;
    if (!h.value) return null;
    return h.unit === 'cm' ? h.value / 100 : (h.value * 2.54) / 100;
  }

  function bmi() {
    var m = state.metrics;
    var hm = heightMetres();
    if (!hm || !m.currentWeight.value) return 0;
    var kg = m.currentWeight.unit === 'kg' ? m.currentWeight.value : m.currentWeight.value * LB_PER_KG;
    return kg / (hm * hm);
  }

  function bmiCategory(v) {
    if (v < 18.5) return 'underweight';
    if (v < 25) return 'normal';
    if (v < 30) return 'overweight';
    return 'obese';
  }

  /** Position on the 15–40 scale, matching the 001 funnel's segment mapping exactly. */
  function bmiProgress(v) {
    var segs = [
      { min: 15, max: 18.5, start: 0, end: 0.25 },
      { min: 18.5, max: 25, start: 0.25, end: 0.5 },
      { min: 25, max: 30, start: 0.5, end: 0.75 },
      { min: 30, max: 40, start: 0.75, end: 1 },
    ];
    for (var i = 0; i < segs.length; i++) {
      if (v <= segs[i].max) {
        var t = (v - segs[i].min) / (segs[i].max - segs[i].min);
        return Math.max(0, Math.min(1, segs[i].start + t * (segs[i].end - segs[i].start)));
      }
    }
    return 1;
  }

  /** Higher of a 12% drop and BMI 18.5, in the current weight unit — see header note. */

  /* ─── Dates ──────────────────────────────────────────────────────────────── */

  /** DD.MM.YYYY -> Date, rejecting impossible dates like 31.02. */
  function parseMaskedDate(masked) {
    var d = String(masked).replace(/\D/g, '');
    if (d.length !== 8) return null;
    var day = +d.slice(0, 2), month = +d.slice(2, 4), year = +d.slice(4, 8);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    var date = new Date(year, month - 1, day);
    // The Date constructor rolls 31 Feb over to March, so compare it back.
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) return null;
    return date;
  }

  function maskDate(raw) {
    var d = String(raw).replace(/\D/g, '').slice(0, 8);
    var out = d.slice(0, 2);
    if (d.length > 2) out += '.' + d.slice(2, 4);
    if (d.length > 4) out += '.' + d.slice(4, 8);
    return out;
  }

  function formatMasked(date) {
    if (!(date instanceof Date) || isNaN(date)) return '';
    return String(date.getDate()).padStart(2, '0') + '.' +
      String(date.getMonth() + 1).padStart(2, '0') + '.' + date.getFullYear();
  }

  function ageFrom(date) {
    return Math.floor((Date.now() - date.getTime()) / 31557600000);
  }

  function ageGroupFor(age) {
    return age < 30 ? '18-29' : age < 40 ? '30-39' : age < 50 ? '40-49' : '50+';
  }

  /** YYYY-MM-DD from LOCAL parts — see the header note on isoDate. */
  function isoDate(d) {
    if (!(d instanceof Date) || isNaN(d)) return undefined;
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  /* ─── Payload ────────────────────────────────────────────────────────────── */

  function up(v) { return v ? String(v).toUpperCase() : undefined; }
  function upAll(v) { return v && v.length ? v.map(function (x) { return String(x).toUpperCase(); }) : undefined; }

  function mapPayload() {
    var s = state;
    var payload = {
      // Hardcoded: the funnel is women-only and Pilates-only, neither is asked, both are
      // required by the API.
      gender: 'FEMALE',
      activities: ['PILATES'],

      heightUnit: s.metrics.height.unit === 'cm' ? 'CM' : 'IN',
      weightUnit: s.metrics.currentWeight.unit === 'kg' ? 'KG' : 'LB',
      height: s.metrics.height.value,
      currentWeight: s.metrics.currentWeight.value,
      goalWeight: s.metrics.goalWeight.value,

      dateOfBirth: isoDate(s.aboutYou.birthday),

      username: s.profile.username,
      bio: s.profile.bio || '',
      displayName: s.plan.displayName || undefined,

      mainGoal: up(s.aboutYou.mainGoal),
      idealWeightPeriod: up(s.aboutYou.idealWeightPeriod),
      focusAreas: upAll(s.aboutYou.focusAreas),
      bodyType: up(s.aboutYou.bodyType),
      dreamBodyShape: up(s.aboutYou.dreamBodyShape),

      typicalDay: up(s.lifestyle.typicalDay),
      energyLevel: up(s.lifestyle.energyLevel),
      workoutFrequency: up(s.lifestyle.workoutFrequency),
      sleepDuration: up(s.lifestyle.sleepDuration),
      flexibilityLevel: up(s.lifestyle.flexibilityLevel),
      stairsBreathLevel: up(s.lifestyle.stairsBreathLevel),
      postureLevel: up(s.lifestyle.postureLevel),
      walkFrequency: up(s.lifestyle.walkFrequency),
      workSchedule: up(s.lifestyle.workSchedule),
      dailySittingTime: up(s.lifestyle.dailySittingTime),
      dailyWaterIntake: up(s.lifestyle.dailyWaterIntake),
      foodHabits: upAll(s.lifestyle.foodHabits),
      weightGainTriggers: upAll(s.lifestyle.weightGainTriggers),
      marketingEmailsOptIn: s.lifestyle.marketingEmailsOptIn == null ? undefined : s.lifestyle.marketingEmailsOptIn,

      breakfastTime: up(s.nutrition.breakfastTime),
      lunchTime: up(s.nutrition.lunchTime),
      dinnerTime: up(s.nutrition.dinnerTime),
      dietType: up(s.nutrition.dietType),

      secondaryGoals: upAll(s.plan.secondaryGoals),
      specialEvent: up(s.plan.specialEvent),
      specialEventDate: isoDate(s.plan.specialEventDate),

      pilatesExperience: up(s.aboutYou.pilatesExperience),
      weightPattern: up(s.aboutYou.weightPattern),
      reformerExperience: up(s.aboutYou.reformerExperience),
      reformerSkipReasons: upAll(s.aboutYou.reformerSkipReasons),
      physicalStruggles: upAll(s.aboutYou.physicalStruggles),
      healthDataConsent: s.auth.healthDataConsent || undefined,
      termsAccepted: s.auth.termsAccepted || undefined,

      quizCountry: s.auth.quizCountry || undefined,
      referralCode: s.auth.referralCode || undefined,
      affiliateCode: s.auth.affiliateCode || undefined,
    };

    // The API validates optional fields when present, so an explicit undefined fails where
    // an absent key passes. username and bio are required and stay even when empty.
    Object.keys(payload).forEach(function (k) {
      if (payload[k] === undefined && k !== 'username' && k !== 'bio') delete payload[k];
    });
    return payload;
  }

  load();

  NS.store = {
    get state() { return state; },
    set: set,
    save: save,
    reset: reset,
  };

  NS.calc = {
    toFeetInches: toFeetInches,
    formatHeight: formatHeight,
    setHeightUnit: setHeightUnit,
    setWeightUnit: setWeightUnit,
    bmi: bmi,
    bmiCategory: bmiCategory,
    bmiProgress: bmiProgress,
    parseMaskedDate: parseMaskedDate,
    maskDate: maskDate,
    formatMasked: formatMasked,
    ageFrom: ageFrom,
    ageGroupFor: ageGroupFor,
    isoDate: isoDate,
    mapPayload: mapPayload,
  };
})(window.ReformlyQuiz);
